import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const BULLETS = [
  {
    n: "01",
    title: "WE AGREE THE TARGET TOGETHER.",
    body: "Your KPI, your number. Written down on day one.",
  },
  {
    n: "02",
    title: "STANDARD FEES. STANDARD ENGAGEMENT.",
    body: "The 90-day clock starts at kickoff.",
  },
  {
    n: "03",
    title: "HIT THE TARGET, ENGAGEMENT CONTINUES.",
    body: "We've done what we said we'd do.",
  },
  {
    n: "04",
    title: "MISS THE TARGET, FEES COME BACK.",
    body: "You keep the work, the assets, and the learnings.",
  },
];

/**
 * Editorial guarantee section — left column (label, headline, intro, bullets),
 * right column (oversized rotating "Corsa Performance Guarantee" stamp).
 */
export const GuaranteeSteps = () => {
  const reduce = useReducedMotion();
  const [hover, setHover] = useState(false);

  return (
    <section
      id="how-it-works"
      data-testid="guarantee-steps-section"
      className="relative grain overflow-hidden"
      style={{ background: "var(--cream)", color: "var(--track)" }}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left — label, headline, intro, bullets */}
          <div className="lg:col-span-7 relative z-10">
            <div
              className="inline-flex items-center gap-2 mb-5 font-semibold uppercase text-xs md:text-sm"
              style={{ color: "var(--nineeleven)" }}
            >
              <span className="dot-marker" />
              <span>The Process</span>
            </div>

            <h2
              data-testid="guarantee-heading"
              className="kuunari-italic"
              style={{ color: "var(--track)" }}
            >
              <span
                className="block text-[clamp(2.6rem,6.2vw,5.6rem)]"
                style={{ lineHeight: 0.94 }}
              >
                {"How the Guarantee Works".split(" ").map((w, i) => (
                  <motion.span
                    key={i}
                    initial={{ y: 18, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{
                      duration: 0.55,
                      delay: i * 0.05,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="inline-block mr-[0.22em]"
                  >
                    {w}
                  </motion.span>
                ))}
              </span>
            </h2>

            <p
              className="mt-6 text-base md:text-lg max-w-xl"
              style={{ color: "var(--track)", opacity: 0.78 }}
              data-testid="guarantee-subline"
            >
              Four steps. No fine print, no theatrics. Here&apos;s exactly how the Corsa Performance Guarantee works in practice.
            </p>

            {/* Bullets — racing checkpoints */}
            <ol
              className="mt-12 md:mt-14 space-y-8 md:space-y-10"
              data-testid="guarantee-bullets"
            >
              {BULLETS.map((b, i) => (
                <motion.li
                  key={b.n}
                  data-testid={`guarantee-bullet-${i + 1}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{
                    duration: 0.55,
                    delay: 0.15 + i * 0.12,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="relative grid grid-cols-[auto_1fr] gap-x-5 md:gap-x-7 items-start"
                >
                  {/* checkpoint marker */}
                  <div className="flex flex-col items-center pt-2" aria-hidden="true">
                    <span
                      className="kuunari-italic"
                      style={{
                        fontSize: "clamp(2.2rem, 4.4vw, 3.6rem)",
                        color: "var(--track)",
                        letterSpacing: "-0.01em",
                        lineHeight: 0.9,
                      }}
                    >
                      {b.n}
                    </span>
                  </div>

                  <div className="pt-1">
                    <h3
                      className="kuunari-italic"
                      style={{
                        color: "var(--track)",
                        fontSize: "clamp(1.4rem, 2.4vw, 1.95rem)",
                        lineHeight: 1.04,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {b.title}
                    </h3>
                    <p
                      className="mt-2 text-sm md:text-base max-w-xl"
                      style={{ color: "var(--track)", opacity: 0.72, lineHeight: 1.55 }}
                    >
                      {b.body}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>

          {/* Right — oversized rotating stamp */}
          <div className="lg:col-span-5 relative">
            <GuaranteeStamp reduce={reduce} hover={hover} setHover={setHover} />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Stamp ---------- */

const GuaranteeStamp = ({ reduce, hover, setHover }) => {
  const [inView, setInView] = useState(false);

  // The text on the ring repeats twice and curves around the full circle.
  const RING_TEXT = "THE CORSA GUARANTEE · ";

  return (
    <motion.div
      data-testid="guarantee-stamp"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      initial={{ opacity: 0, scale: 0.85 }}
      whileInView={{ opacity: 1, scale: 1 }}
      onViewportEnter={() => setInView(true)}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="relative mx-auto lg:mx-0"
      style={{
        // crop into right edge on desktop for editorial feel
        width: "min(120%, 720px)",
        aspectRatio: "1 / 1",
        marginRight: "calc(var(--stamp-overflow, -8%))",
      }}
    >
      {/* checker / racing accents around stamp */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" />

      {/* Rotating outer text ring */}
      <motion.div
        className="absolute inset-0"
        animate={reduce ? {} : { rotate: 360 }}
        transition={
          reduce
            ? undefined
            : {
                repeat: Infinity,
                ease: "linear",
                duration: hover ? 14 : 28,
              }
        }
        aria-hidden="true"
      >
        <svg viewBox="0 0 400 400" className="w-full h-full block">
          <defs>
            <path
              id="stamp-ring-path"
              d="M 200,200 m -170,0 a 170,170 0 1,1 340,0 a 170,170 0 1,1 -340,0"
            />
          </defs>
          <text
            fontFamily="Kuunari, 'Archivo Black', Impact, sans-serif"
            fontWeight="900"
            fontStyle="italic"
            fontSize="22"
            letterSpacing="6"
            fill="var(--track)"
          >
            <textPath xlinkHref="#stamp-ring-path" startOffset="0">
              {RING_TEXT + RING_TEXT + RING_TEXT}
            </textPath>
          </text>
        </svg>
      </motion.div>

      {/* Static rings + center mark */}
      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full block" aria-hidden="true">
        {/* outermost thin track ring */}
        <circle cx="200" cy="200" r="194" fill="none" stroke="var(--track)" strokeWidth="2" />
        {/* inner ring boundary */}
        <circle cx="200" cy="200" r="148" fill="none" stroke="var(--track)" strokeWidth="2" />
        {/* dashed mid ring (racing finish-line feel) */}
        <circle
          cx="200"
          cy="200"
          r="160"
          fill="none"
          stroke="var(--track)"
          strokeWidth="1.5"
          strokeDasharray="2 6"
          opacity="0.55"
        />

        {/* Four cardinal racing-stripe ticks — removed per request */}

        {/* central calypso disc */}
        <circle cx="200" cy="200" r="118" fill="var(--calypso)" stroke="var(--track)" strokeWidth="3" />

        {/* checker-flag horizontal band */}
        <g transform="translate(120 195)">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <rect
              key={i}
              x={i * 20}
              y={0}
              width={20}
              height={10}
              fill={i % 2 === 0 ? "var(--track)" : "var(--cream)"}
            />
          ))}
        </g>

        {/* Centre headline text — two lines */}
        <text
          x="200"
          y="178"
          textAnchor="middle"
          fontFamily="Kuunari, 'Archivo Black', Impact, sans-serif"
          fontWeight="900"
          fontStyle="italic"
          fontSize="46"
          fill="var(--track)"
          letterSpacing="-1"
        >
          90 DAY
        </text>
        <text
          x="200"
          y="248"
          textAnchor="middle"
          fontFamily="Kuunari, 'Archivo Black', Impact, sans-serif"
          fontWeight="900"
          fontStyle="italic"
          fontSize="34"
          fill="var(--track)"
          letterSpacing="-1"
        >
          GUARANTEE
        </text>

        {/* small bottom dot removed */}
      </svg>

      {/* subtle hover pulse halo */}
      <motion.div
        className="absolute inset-0 rounded-full pointer-events-none"
        animate={hover && !reduce ? { scale: [1, 1.04, 1], opacity: [0.0, 0.18, 0.0] } : { scale: 1, opacity: 0 }}
        transition={{ duration: 1.6, ease: "easeInOut", repeat: hover && !reduce ? Infinity : 0 }}
        style={{ background: "radial-gradient(closest-side, var(--calypso) 0%, transparent 70%)" }}
        aria-hidden="true"
      />

      <span className="sr-only">{inView ? "" : ""}The Corsa Performance Guarantee. 90 day guarantee.</span>
    </motion.div>
  );
};

export default GuaranteeSteps;
